
import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSearchResultsFilter } from '@/hooks/useSearchResultsFilter';
import { useAmplitudeEvents } from '@/utils/amplitudeEvents';

export const useSearchResultsLogic = () => {
  const [searchParams] = useSearchParams();
  const { filteredProperties, isAnyLoading, hasErrors, refetchAll } = useSearchResultsFilter();
  const { trackSearchResultDisplayed } = useAmplitudeEvents();
  
  // Use ref to track if event has been fired for current search
  const eventTrackedRef = useRef<string>('');
  // Add ref to track the previous loading state
  const wasLoadingRef = useRef(true);

  const guests = searchParams.get('guests');
  const checkin = searchParams.get('checkin');
  const checkout = searchParams.get('checkout');

  // Track search result displayed only once per unique search - improved logic
  useEffect(() => {
    const searchKey = `${guests}-${checkin}-${checkout}`;
    
    // Only fire when:
    // 1. Loading just finished (was loading, now not loading)
    // 2. We have search parameters
    // 3. We haven't fired for this search combination yet
    if (wasLoadingRef.current && !isAnyLoading && (checkin || checkout || guests)) {
      if (eventTrackedRef.current !== searchKey) {
        console.log('🎯 Firing search result displayed event:', {
          searchKey,
          resultsCount: filteredProperties.length,
          wasLoading: wasLoadingRef.current,
          isLoading: isAnyLoading
        });
        
        trackSearchResultDisplayed(filteredProperties.length);
        eventTrackedRef.current = searchKey;
      }
    }
    
    // Update the loading state reference
    wasLoadingRef.current = isAnyLoading;
  }, [filteredProperties.length, isAnyLoading, checkin, checkout, guests, trackSearchResultDisplayed]);

  return {
    filteredProperties,
    isAnyLoading,
    hasErrors,
    refetchAll,
    guests,
    checkin,
    checkout
  };
};
